// src/components/Creator/AuthorProfileForm.jsx
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Upload, Loader2, User, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

const AuthorProfileForm = ({ isOpen, onClose, profile }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    pen_name: '',
    website_url: '',
    publisher_name: '',
    publisher_url: '',
    contact_email: '',
    bio_text: '',
    is_public: false
  });
  
  const [uploading, setUploading] = useState(false);
  const [uploadingType, setUploadingType] = useState(null); // Track which type is uploading
  const [saving, setSaving] = useState(false);
  const [headshotPreview, setHeadshotPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // BACKEND_URL imported from centralized config

  // Only load profile data on initial open, not on every profile change
  useEffect(() => {
    if (isOpen && profile && !initialLoadDone) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        pen_name: profile.pen_name || '',
        website_url: profile.website_url || '',
        publisher_name: profile.publisher_name || '',
        publisher_url: profile.publisher_url || '',
        contact_email: profile.contact_email || '',
        bio_text: profile.bio_raw_source?.text || '',
        is_public: profile.is_public || false
      });
      
      // Load existing images if available
      if (profile.headshot_image_url) {
        setHeadshotPreview(`${BACKEND_URL}${profile.headshot_image_url}?t=${Date.now()}`);
      }
      if (profile.logo_image_url) {
        setLogoPreview(`${BACKEND_URL}${profile.logo_image_url}?t=${Date.now()}`);
      }
      setInitialLoadDone(true);
    }
    
    // Reset when modal closes
    if (!isOpen) {
      setInitialLoadDone(false);
      setHeadshotPreview(null);
      setLogoPreview(null);
    }
  }, [isOpen, profile, BACKEND_URL, initialLoadDone]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (checked) => {
    setFormData(prev => ({ ...prev, is_public: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.bio_text) {
      toast.error('Please fill in required fields: First Name, Last Name, and Bio');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const url = profile 
        ? `${BACKEND_URL}/api/creator/author-profile`
        : `${BACKEND_URL}/api/creator/author-profile`;
      
      const method = profile ? 'put' : 'post';
      
      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(profile ? 'Profile updated successfully' : 'Profile created successfully');
      onClose();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadBioDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.docx')) {
      toast.error('Please upload a .docx file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/creator/author-profile/upload-bio-doc`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setFormData(prev => ({
        ...prev,
        bio_text: response.data.bio_raw_source.text
      }));
      
      toast.success('Bio document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadMedia = async (e, mediaType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    if (mediaType === 'headshot') {
      setHeadshotPreview(previewUrl);
    } else {
      setLogoPreview(previewUrl);
    }

    setUploading(true);
    setUploadingType(mediaType);
    
    try {
      const token = localStorage.getItem('accessToken');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('media_type', mediaType);

      const response = await axios.post(
        `${BACKEND_URL}/api/creator/author-profile/upload-media`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`${mediaType === 'headshot' ? 'Headshot' : 'Logo'} uploaded successfully`);
      
      // Update preview with server URL from returned profile
      const updatedProfile = response.data;
      if (mediaType === 'headshot' && updatedProfile?.headshot_image_url) {
        setHeadshotPreview(`${BACKEND_URL}${updatedProfile.headshot_image_url}?t=${Date.now()}`);
      } else if (mediaType === 'logo' && updatedProfile?.logo_image_url) {
        setLogoPreview(`${BACKEND_URL}${updatedProfile.logo_image_url}?t=${Date.now()}`);
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload media');
      // Revert preview on error
      if (mediaType === 'headshot') {
        setHeadshotPreview(profile?.headshot_image_url ? `${BACKEND_URL}${profile.headshot_image_url}` : null);
      } else {
        setLogoPreview(profile?.logo_image_url ? `${BACKEND_URL}${profile.logo_image_url}` : null);
      }
    } finally {
      setUploading(false);
      setUploadingType(null);
      // Clear the file input so the same file can be selected again
      e.target.value = '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 text-white border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {profile ? 'Edit Author Profile' : 'Create Author Profile'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-200">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-slate-300">
                  First Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="last_name" className="text-slate-300">
                  Last Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pen_name" className="text-slate-300">
                Pen Name (optional)
              </Label>
              <Input
                id="pen_name"
                name="pen_name"
                value={formData.pen_name}
                onChange={handleChange}
                placeholder="Leave empty to use your real name"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          {/* Profile Images - Moved to be more prominent */}
          {profile ? (
            <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-200">Profile Images</h3>
              <p className="text-sm text-slate-400">Upload your author headshot and logo images</p>
              
              <div className="grid grid-cols-2 gap-6">
                {/* Headshot Upload */}
                <div className="space-y-3">
                  <Label className="text-slate-300 block">Author Headshot</Label>
                  <div className="relative text-center">
                    {headshotPreview ? (
                      <div className="relative w-28 h-28 mx-auto mb-3">
                        <img 
                          src={headshotPreview} 
                          alt="Headshot preview" 
                          className="w-full h-full object-cover rounded-full border-2 border-purple-500"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-28 h-28 mx-auto mb-3 rounded-full bg-slate-700 flex items-center justify-center border-2 border-dashed border-slate-600">
                        <User className="w-10 h-10 text-slate-500" />
                      </div>
                    )}
                    <Input
                      id="headshot-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadMedia(e, 'headshot')}
                      className="hidden"
                      disabled={uploadingType === 'headshot'}
                    />
                    <Label
                      htmlFor="headshot-upload"
                      className={`cursor-pointer inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm text-white transition-colors ${uploadingType === 'headshot' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadingType === 'headshot' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      {headshotPreview ? 'Change' : 'Upload'}
                    </Label>
                  </div>
                </div>
                
                {/* Logo Upload */}
                <div className="space-y-3">
                  <Label className="text-slate-300 block">Author/Publisher Logo</Label>
                  <div className="relative text-center">
                    {logoPreview ? (
                      <div className="relative w-28 h-28 mx-auto mb-3">
                        <img 
                          src={logoPreview} 
                          alt="Logo preview" 
                          className="w-full h-full object-contain rounded-lg border-2 border-cyan-500 bg-white p-1"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-28 h-28 mx-auto mb-3 rounded-lg bg-slate-700 flex items-center justify-center border-2 border-dashed border-slate-600">
                        <Building2 className="w-10 h-10 text-slate-500" />
                      </div>
                    )}
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadMedia(e, 'logo')}
                      className="hidden"
                      disabled={uploadingType === 'logo'}
                    />
                    <Label
                      htmlFor="logo-upload"
                      className={`cursor-pointer inline-flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg text-sm text-white transition-colors ${uploadingType === 'logo' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadingType === 'logo' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      {logoPreview ? 'Change' : 'Upload'}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 border-dashed">
              <div className="flex items-center gap-3 text-slate-400">
                <User className="w-6 h-6" />
                <div>
                  <p className="text-sm font-medium text-slate-300">Profile Images</p>
                  <p className="text-xs">Save your profile first, then you can upload your headshot and logo images.</p>
                </div>
              </div>
            </div>
          )}

          {/* Website & Publisher */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-200">Professional Links</h3>
            
            <div>
              <Label htmlFor="website_url" className="text-slate-300">
                Website URL
              </Label>
              <Input
                id="website_url"
                name="website_url"
                type="url"
                value={formData.website_url}
                onChange={handleChange}
                placeholder="https://yourwebsite.com"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="publisher_name" className="text-slate-300">
                  Publisher Name
                </Label>
                <Input
                  id="publisher_name"
                  name="publisher_name"
                  value={formData.publisher_name}
                  onChange={handleChange}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="publisher_url" className="text-slate-300">
                  Publisher URL
                </Label>
                <Input
                  id="publisher_url"
                  name="publisher_url"
                  type="url"
                  value={formData.publisher_url}
                  onChange={handleChange}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="contact_email" className="text-slate-300">
                Contact Email (private)
              </Label>
              <Input
                id="contact_email"
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="This will not be shown to readers"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          {/* Bio */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-200">
                Biography <span className="text-red-400">*</span>
              </h3>
              
              {profile && (
                <div>
                  <Input
                    id="bio-upload"
                    type="file"
                    accept=".docx"
                    onChange={handleUploadBioDoc}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Label
                    htmlFor="bio-upload"
                    className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm"
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Upload .docx
                  </Label>
                </div>
              )}
            </div>
            
            <Textarea
              name="bio_text"
              value={formData.bio_text}
              onChange={handleChange}
              required
              rows={8}
              placeholder="Tell readers about yourself, your writing journey, and what inspires you..."
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>

          {/* Visibility */}
          <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
            <div>
              <Label htmlFor="is_public" className="text-slate-200 font-semibold">
                Make profile visible to readers
              </Label>
              <p className="text-sm text-slate-400 mt-1">
                When enabled, readers will see your profile on your books
              </p>
            </div>
            <Switch
              id="is_public"
              checked={formData.is_public}
              onCheckedChange={handleSwitchChange}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                profile ? 'Update Profile' : 'Create Profile'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthorProfileForm;
